import styled from '@emotion/styled';

import StatusCell from '../../../components/Badge/StatusCell';
import TableSliderContentWrapper from '../../../components/Table/TableAccessories/TableSliderContentWrapper';
import useCustomerProfile from '../../../hooks/dashboard/risk/useCustomerProfile';
import { isActiveStatus } from '../../../models/risk/risk.model';
import { capitalize, formatNaira, tableFormatDateTimeNG } from '../../../utils/utils';

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <DetailRow>
    <DetailLabel>{label}</DetailLabel>
    <DetailValue>{children}</DetailValue>
  </DetailRow>
);

const formatDate = (value?: string | null) => (value ? tableFormatDateTimeNG(value) : '—');

const scoreLevel = (score: number) =>
  score >= 60 ? 'CRITICAL' : score >= 30 ? 'HIGH' : score >= 10 ? 'MEDIUM' : 'LOW';

const CustomerProfileSlider: React.FC<{ userId: string }> = ({ userId }) => (
  <TableSliderContentWrapper label='Customer 360'>
    <CustomerProfileBody userId={userId} />
  </TableSliderContentWrapper>
);

export default CustomerProfileSlider;

// The 360 view without its slider frame, so the alert slider can embed it.
export const CustomerProfileBody: React.FC<{ userId: string }> = ({ userId }) => {
  const { profile, loading } = useCustomerProfile(userId);

  return (
    <>
      {loading || !profile ? (
        <LoadingText>Loading…</LoadingText>
      ) : (
        <>
          <Section>
            <HeaderRow>
              <div>
                <NameText>{profile.display_name ?? profile.chat_id}</NameText>
                <MutedText>{profile.chat_id}</MutedText>
              </div>
              <ScoreBox>
                <ScoreValue>{profile.risk_score}</ScoreValue>
                <StatusCell
                  data={{ level: scoreLevel(profile.risk_score) }}
                  statusKey='level'
                />
              </ScoreBox>
            </HeaderRow>
            <Row label='Email'>{profile.email ?? '—'}</Row>
            <Row label='Account status'>{profile.is_active ? 'Active' : 'Disabled'}</Row>
            <Row label='Joined'>{formatDate(profile.created_at)}</Row>
            <Row label='Onboarded'>{formatDate(profile.onboarding_completed_at)}</Row>
            <Row label='PIN lockouts'>
              {profile.pin_lockout_count}
              {profile.pin_locked_until ? ' (locked)' : ''}
            </Row>
          </Section>

          <Section>
            <SectionTitle>Card on file</SectionTitle>
            {profile.card ? (
              <>
                <Row label='Card'>
                  {profile.card.card_type.toUpperCase()} •••• {profile.card.last4}
                </Row>
                <Row label='Bank'>{profile.card.bank}</Row>
                <Row label='Card email'>{profile.card.email}</Row>
                <Row label='Card fingerprint'>
                  {profile.card.fingerprint_captured
                    ? 'Captured — exact duplicate-card checks'
                    : 'Not captured yet — duplicate checks use bank + last 4'}
                </Row>
                <Row label='Added'>{formatDate(profile.card.added_at)}</Row>
              </>
            ) : (
              <MutedText>No card saved</MutedText>
            )}
          </Section>

          <Section>
            <SectionTitle>Behaviour</SectionTitle>
            <StatGrid>
              <Stat label='Transactions' value={profile.stats.total_transactions} />
              <Stat label='Successful' value={profile.stats.successful} />
              <Stat label='Declined' value={profile.stats.declined} />
              <Stat label='Unresolved' value={profile.stats.unresolved} />
              <Stat label='Total spent' value={formatNaira(Number(profile.stats.total_spent))} />
              <Stat label='Average' value={formatNaira(Number(profile.stats.average_amount))} />
              <Stat label='Largest' value={formatNaira(Number(profile.stats.largest_amount))} />
              <Stat label='Recipients' value={profile.stats.distinct_recipients} />
              <Stat label='Ejors sent' value={profile.stats.ejors_initiated} />
              <Stat label='Ejors paid' value={profile.stats.ejors_paid} />
            </StatGrid>
            <Row label='First purchase'>{formatDate(profile.stats.first_transaction_at)}</Row>
            <Row label='Last purchase'>{formatDate(profile.stats.last_transaction_at)}</Row>
          </Section>

          <Section>
            <SectionTitle>
              Alerts · {profile.alerts.filter((a) => isActiveStatus(a.status)).length} need
              attention
            </SectionTitle>
            {profile.alerts.length === 0 && <MutedText>No alerts</MutedText>}
            {profile.alerts.map((item) => (
              <ListRow key={item.id}>
                <StatusCell data={item} statusKey='severity' />
                <div>
                  <strong>{item.title}</strong>
                  <MutedText>{item.detail}</MutedText>
                </div>
                <StatusCell data={item} statusKey='status' />
              </ListRow>
            ))}
          </Section>

          <Section>
            <SectionTitle>Linked accounts · {profile.linked_accounts.length}</SectionTitle>
            {profile.linked_accounts.length === 0 && (
              <MutedText>No shared card, email, recipient or Ejor links</MutedText>
            )}
            {profile.linked_accounts.map((link) => (
              <DetailRow key={link.user_id}>
                <DetailLabel>{link.display_name ?? link.chat_id}</DetailLabel>
                <DetailValue>{link.link}</DetailValue>
              </DetailRow>
            ))}
          </Section>

          <Section>
            <SectionTitle>Recent transactions</SectionTitle>
            {profile.recent_transactions.length === 0 && <MutedText>None yet</MutedText>}
            {profile.recent_transactions.map((txn) => (
              <TxnRow key={txn.reference}>
                <span>
                  {capitalize(txn.type.replace('_', ' '))}
                  <MutedText>{txn.recipient?.split(':')[1] ?? txn.provider}</MutedText>
                </span>
                <span>{formatNaira(Number(txn.amount))}</span>
                <StatusCell data={txn} statusKey='status' hasDot positiveType />
                <MutedText>{formatDate(txn.created_at)}</MutedText>
              </TxnRow>
            ))}
          </Section>

          {profile.recent_events.length > 0 && (
            <Section>
              <SectionTitle>Account events</SectionTitle>
              {profile.recent_events.map((event, index) => (
                <DetailRow key={index}>
                  <DetailLabel>{event.event_type}</DetailLabel>
                  <DetailValue>{formatDate(event.created_at)}</DetailValue>
                </DetailRow>
              ))}
            </Section>
          )}
        </>
      )}
    </>
  );
};

const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <StatBox>
    <MutedText>{label}</MutedText>
    <strong>{value}</strong>
  </StatBox>
);

const LoadingText = styled.div`
  color: #5D665F;
  font-size: 14px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 20px;
  border-bottom: 1px solid #E2DED3;

  &:last-of-type {
    border-bottom: none;
  }
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #5D665F;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const NameText = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #0E1A13;
`;

const ScoreBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const ScoreValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #0E1A13;
  line-height: 1;
`;

const MutedText = styled.div`
  font-size: 12px;
  color: #5D665F;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
`;

const DetailLabel = styled.span`
  color: #5D665F;
`;

const DetailValue = styled.span`
  color: #0E1A13;
  font-weight: 500;
  text-align: right;
  word-break: break-all;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 8px;
`;

const StatBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px;
  border: 1px solid #E2DED3;
  border-radius: 6px;
  font-size: 14px;
  color: #0E1A13;
`;

const ListRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 10px;
  align-items: flex-start;
  font-size: 14px;
  color: #0E1A13;
`;

const TxnRow = styled.div`
  display: grid;
  grid-template-columns: 1.3fr 1fr auto 1.2fr;
  gap: 8px;
  align-items: center;
  font-size: 13px;
  color: #0E1A13;

  @media (max-width: 560px) {
    grid-template-columns: 1fr 1fr;
  }
`;
